import { useState, useMemo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useChildAssets, useHabitability, useAssetProperties, useAssetTypes, useContracts, usePropertyDefinitionsByCodes } from '../hooks/useQueries'
import type { Asset, AssetPropertyValue, AssetType, PropertyDefinition } from '../hooks/useQueries'
import { Skeleton } from './ui/skeleton'
import { Button } from './ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { Link } from '@tanstack/react-router'
import { mdiArrowUpDownBold, mdiChevronDown, mdiClose, mdiPencil } from '@mdi/js'
import { $api } from '../api/queryClient'
import { COLUMN_BAR_GAP, COLUMN_BAR_WIDTH, COLUMN_LANE_PAD, layoutStackingColumns } from './plan/stacking-columns'
import type { RiserDiagram as RiserDiagramPayload } from './plan/riser-diagram-layout'
import { AddColumnDialog } from './plan/AddColumnDialog'
import { ThematicBar, useThematic } from './thematics/ThematicBar'
import { isDimmed, NOT_APPLICABLE, UNSET, UNSET_COLOUR, pickLabel } from './thematics/colour'
import { AppIcon } from './ui/icon'
import { AddStackingEntityDialog, type StackingEntityKind } from './AddStackingEntityDialog'
import { formatDisplayNumber } from '../lib/format'
import { EmptyState } from './ui/empty-state'

// ─────────────────────────────────────────────────────
//  PROPS
// ─────────────────────────────────────────────────────
interface StackingPlanProps {
  org: string
  buildingUid: string
}

// ─────────────────────────────────────────────────────
//  DESIGN TOKENS (CSS variable references for theme support)
// ─────────────────────────────────────────────────────
const V = {
  foreground: 'var(--foreground)',
  mutedForeground: 'var(--muted-foreground)',
  muted: 'var(--muted)',
  border: 'var(--border)',
  card: 'var(--card)',
  accent: 'var(--accent)',
  input: 'var(--input)',
  popover: 'var(--popover)',
  popoverForeground: 'var(--popover-foreground)',
}

// Habitability. Only the two states worth interrupting the reader get a colour:
// a lot that cannot lawfully be let, and one whose assessment is incomplete —
// which is not the same as fit, and must not be painted as if it were.
const LET_COLOR: Record<string, string> = {
  ok: '#2ECC91',
  forbidden: '#F05252',
  unknown: '#F0B429',
}

// ─────────────────────────────────────────────────────
//  SVG LAYOUT (viewBox units)
// ─────────────────────────────────────────────────────
const VW = 940
const RH = 70
const RG = 5
const LW = 52
const AW = 66
const SX = LW + 4
const SW = VW - SX - AW - 4
const VP = 20

/** The colour of a column whose risers carry no network (declared only). */
const COLUMN_TYPE_COLOUR: Record<string, string> = {
  fire_riser: 'var(--destructive)',
  elec_riser: 'var(--warning)',
}

// ─────────────────────────────────────────────────────
//  TYPES
// ─────────────────────────────────────────────────────
export interface Unit {
  uid: string
  id: string
  type: 'residential' | 'commercial' | 'common_area' | 'technical'
  area: number
  // Co-ownership shares (tantièmes). Used as a fallback weight when
  // surface_area is missing — the user's report (#938) was that condo
  // units often have only shares declared, not surfaces.
  shares: number | null
  // Read off `unit_is_occupied`, the one occupancy of a lot (spec 2026-10-07-01).
  occupation: 'occupied' | 'vacant' | null
  compliance: 'compliant' | 'warning' | 'critical' | 'unknown'
  // May the lot lawfully be let? `null` means the question does not apply —
  // no tenancy of a kind the habitability rules cover, so the plan stays
  // silent rather than showing a verdict nobody asked for.
  lettable: 'ok' | 'forbidden' | 'unknown' | null
  tenant: string | null
  leaseEnd: string | null
}

interface PreparedUnit extends Unit {
  _x: number
  _w: number
}

export interface Floor {
  level: number
  label: string
  // The floor asset the row's label links to (#1489). Rows merge every floor
  // asset at the same level, so this is the first one in the building's order.
  uid: string
  units: Unit[]
}

interface PreparedFloor extends Omit<Floor, 'units'> {
  _total: number
  _units: PreparedUnit[]
}

// ─────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────
// Width-allocation weight for a unit. Surface area wins when it's set;
// otherwise we fall back to ownership_shares (#938) so condo lots with
// only tantièmes declared still render proportionally. The 10 floor is
// a last-resort placeholder so a unit without either signal still has
// a clickable footprint.
const unitArea = (u: Unit) => {
  if (u.area && u.area > 0) return u.area
  if (u.shares && u.shares > 0) return u.shares
  return 10
}

// `width` is what the lots get: the full span, less the columns lane.
const prepareFloor = (floor: Floor, width: number = SW): PreparedFloor => {
  const total = floor.units.reduce((s, u) => s + unitArea(u), 0)
  const unitGap = RG / 2
  const gapCount = Math.max(0, floor.units.length - 1)
  const usableW = width - gapCount * unitGap
  let x = SX
  return {
    ...floor,
    _total: total,
    _units: floor.units.map((u, i) => {
      const w = (unitArea(u) / total) * usableW
      const r: PreparedUnit = { ...u, _x: x, _w: w }
      x += w + (i < floor.units.length - 1 ? unitGap : 0)
      return r
    }),
  }
}


/**
 * Group the building's floor assets into one row per level, top floor first.
 * Each row links to the first floor asset found at its level (#1489).
 */
export function buildFloorRows(
  floorAssets: Pick<Asset, 'uid'>[] | undefined,
  floorLevels: Map<string, number>,
  floorUnitsMap: Map<string, Pick<Asset, 'uid'>[]>,
  unitDataMap: Map<string, Unit>,
): Floor[] {
  if (!floorAssets || floorAssets.length === 0) return []

  // A stacking plan is one row per physical level. Group/merge every
  // floor-typed asset that resolves to the same level into a single row
  // (concatenating their units) instead of emitting one row per asset —
  // this makes rendering insensitive to any residual duplicate-level data
  // and gives every row a level that is unique post-merge, so it can be
  // used as a stable React key (see render below).
  const byLevel = new Map<number, Unit[]>()
  const floorUidByLevel = new Map<number, string>()
  for (const floorAsset of floorAssets) {
    const level = floorLevels.get(floorAsset.uid)
    if (level === undefined) continue

    const unitAssetsList = floorUnitsMap.get(floorAsset.uid) || []
    const units: Unit[] = []
    for (const ua of unitAssetsList) {
      const unitData = unitDataMap.get(ua.uid)
      if (unitData) {
        units.push(unitData)
      }
    }

    const existing = byLevel.get(level)
    if (existing) {
      existing.push(...units)
    } else {
      byLevel.set(level, units)
      floorUidByLevel.set(level, floorAsset.uid)
    }
  }

  const result: Floor[] = Array.from(byLevel.entries()).map(([level, units]) => ({
    level,
    label: level === 0 ? 'RDC' : level > 0 ? `R+${level}` : `SS${Math.abs(level)}`,
    uid: floorUidByLevel.get(level)!,
    units,
  }))

  // Sort floors descending by level (top floor first)
  result.sort((a, b) => b.level - a.level)

  return result
}

/**
 * Parse a floor level from a human-readable asset name.
 * Falls back to the `floor_number` property when it is set, but most
 * seed data relies on naming conventions like "ground", "level 3",
 * "level -1", "R+2", "RDC", "SS1", etc.
 *
 * Patterns are anchored at the start of the (trimmed, lower-cased) name but
 * tolerate a trailing suffix (e.g. "Étage 3 — Bâtiment 1B"), since real seed
 * data commonly appends a building qualifier after the level token. The
 * loose "first integer anywhere" fallback is a last resort and is only ever
 * reached by callers that have already filtered to `floor`-typed assets —
 * on unfiltered input (e.g. "Ascenseur 1B") it would misfire.
 */
// lettableFromVerdict maps a lot's habitability verdict, read from the
// habitability endpoint, to the plan badge. A lot with no entry is outside the
// habitability scope and the plan shows nothing. "unknown" is never lettable: no
// proof of a breach is not a clean bill (the server files a rule that errored
// or a partial assessment under unknown).
export function lettableFromVerdict(verdict?: 'compliant' | 'non_compliant' | 'unknown'): Unit['lettable'] {
  if (verdict === 'non_compliant') return 'forbidden'
  if (verdict === 'unknown') return 'unknown'
  if (verdict === 'compliant') return 'ok'
  return null
}

export function parseFloorLevelFromName(rawName: string | null | undefined): number | null {
  if (!rawName) return null
  const name = rawName.trim().toLowerCase()
  // "rdc", "rez-de-chaussée", "rez de chaussée…" (with or without trailing suffix)
  if (/^(rdc|rez[\s-]?de[\s-]?chauss)/.test(name)) return 0
  // "R+2", "r-1", "R+2 — Bâtiment 1B"
  const rPlus = name.match(/^r\s*([+-]?\d+)\b/i)
  if (rPlus) return parseInt(rPlus[1], 10)
  // "SS1", "SS2", "Sous-sol 1" → -1, -2 (basement)
  const ss = name.match(/^(?:ss|sous[\s-]?sol)\s*(\d+)\b/i)
  if (ss) return -parseInt(ss[1], 10)
  // "level 3", "level -1", "floor 2", "étage 4", "niveau 2"
  const lvl = name.match(/^(?:level|floor|étage|niveau)\s*(-?\d+)\b/i)
  if (lvl) return parseInt(lvl[1], 10)
  // fallback: any signed integer anywhere in the name
  const loose = name.match(/(-?\d+)/)
  if (loose) return parseInt(loose[1], 10)
  return null
}

// ─────────────────────────────────────────────────────
//  PROPERTY HELPERS
// ─────────────────────────────────────────────────────
function buildPropertyCodeMap(definitions: PropertyDefinition[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const def of definitions) {
    map.set(def.uid, def.code)
  }
  return map
}

/** Maps asset_type_uid → asset type code (e.g. "floor", "trade_lift"). */
function buildAssetTypeCodeMap(assetTypes: AssetType[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const type of assetTypes) {
    map.set(type.uid, type.code)
  }
  return map
}

function getPropertyValue(
  properties: AssetPropertyValue[],
  codeMap: Map<string, string>,
  code: string,
): string | null {
  const prop = properties.find((p) => p.property_uid && codeMap.get(p.property_uid) === code)
  return prop?.value ?? null
}

// ─────────────────────────────────────────────────────
//  SUB-COMPONENTS (pure rendering)
// ─────────────────────────────────────────────────────

function Tooltip({ unit, pos, thematic }: { unit: PreparedUnit; pos: { x: number; y: number }; thematic?: { label: string; colour: string } | null }) {
  const { t } = useTranslation('assets')
  const occLabel: Record<string, string> = {
    occupied: t('stacking_plan.occupation_occupied'),
    vacant: t('stacking_plan.occupation_vacant'),
  }
  const cplLabel: Record<string, string> = {
    compliant: t('stacking_plan.compliance_compliant'),
    warning: t('stacking_plan.compliance_warning'),
    critical: t('stacking_plan.compliance_critical'),
    unknown: t('stacking_plan.compliance_unknown'),
  }
  const typeLabel: Record<string, string> = {
    residential: t('stacking_plan.type_residential'),
    commercial: t('stacking_plan.type_commercial'),
    common_area: t('stacking_plan.type_common_area'),
    technical: t('stacking_plan.type_technical'),
  }
  return (
    <div
      data-testid="stacking-tooltip"
      className="border border-border bg-popover text-popover-foreground shadow-lg"
      style={{
        position: 'fixed',
        left: pos.x + 18,
        top: pos.y - 12,
        borderRadius: 8,
        padding: '12px 14px',
        pointerEvents: 'none',
        zIndex: 1000,
        minWidth: 210,
        maxWidth: 270,
        fontFamily: "var(--font-sans)",
      }}
    >
      <div className="text-foreground" style={{ fontSize: 14, fontWeight: 500, marginBottom: 10, letterSpacing: '-0.01em' }}>{unit.id}</div>
      <TRow label={t('stacking_plan.tooltip_type')} value={typeLabel[unit.type]} />
      <TRow label={t('stacking_plan.tooltip_area')} value={unit.area && unit.area > 0 ? `${formatDisplayNumber(unit.area)} m²` : '\u2014'} />
      {unit.shares != null && (
        <TRow label={t('stacking_plan.tooltip_shares', 'Shares')} value={formatDisplayNumber(unit.shares)} />
      )}
      {unit.occupation && <TRow label={t('stacking_plan.tooltip_occupation')} value={occLabel[unit.occupation]} />}
      <TRow label={t('stacking_plan.tooltip_compliance')} value={cplLabel[unit.compliance]} />
      {thematic && <TRow label={t('stacking_plan.tooltip_thematic', 'Colour')} value={thematic.label} color={thematic.colour} />}
      {unit.lettable && (
        <TRow
          label={t('stacking_plan.tooltip_lettable')}
          value={t(`stacking_plan.lettable_${unit.lettable}`)}
          color={LET_COLOR[unit.lettable]}
        />
      )}
      {unit.tenant && <TRow label={t('stacking_plan.tooltip_tenant')} value={unit.tenant} />}
      {unit.leaseEnd && <TRow label={t('stacking_plan.tooltip_lease_end')} value={unit.leaseEnd} />}
    </div>
  )
}

function TRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
      <span className="text-muted-foreground" style={{ fontSize: 12 }}>{label}</span>
      <span className={color ? '' : 'text-foreground'} style={{ fontSize: 13, color: color || undefined, textAlign: 'right' }}>{value}</span>
    </div>
  )
}

function KPICard({
  label,
  value,
  sub,
  color,
  onClick,
  active,
}: {
  label: string
  value: string | number
  sub?: string
  color?: string
  onClick?: () => void
  active?: boolean
}) {
  return (
    <div
      onClick={onClick}
      data-testid={`kpi-${label}`}
      style={{
        padding: '10px 16px',
        background: active ? V.accent : 'transparent',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: active ? V.border : 'transparent',
        borderRadius: 6,
        cursor: onClick ? 'pointer' : 'default',
        minWidth: 86,
        transition: 'all 0.15s',
      }}
    >
      <div className={color ? '' : 'text-foreground'} style={{ fontSize: 24, fontWeight: 600, fontFamily: "var(--font-sans)", color: color || undefined, lineHeight: 1, letterSpacing: '-0.02em' }}>{value}</div>
      <div className="text-muted-foreground" style={{ fontSize: 12, marginTop: 6, fontWeight: 500 }}>{label}</div>
      {sub && <div className="text-muted-foreground" style={{ fontSize: 12, marginTop: 2, fontFamily: "var(--font-sans)", opacity: 0.7 }}>{sub}</div>}
    </div>
  )
}

// ─────────────────────────────────────────────────────
//  DATA-FETCHING HOOKS
// ─────────────────────────────────────────────────────

/** Hook that fetches properties & contracts for a single unit asset and returns a Unit. */
function useUnitData(
  org: string,
  unitAsset: Asset,
  codeMap: Map<string, string>,
  verdict?: 'compliant' | 'non_compliant' | 'unknown',
): { unit: Unit; isLoading: boolean } {
  const { data: properties, isLoading: propsLoading } = useAssetProperties(org, unitAsset.uid)
  const { data: contracts, isLoading: contractsLoading } = useContracts(org, {
    assetUid: unitAsset.uid,
    runningLease: true,
    include: ['stakeholders', 'stakeholder_type', 'user'],
  })

  const unit = useMemo((): Unit => {
    const unitType = getPropertyValue(properties, codeMap, 'unit_type') as Unit['type'] | null
    const surfaceArea = getPropertyValue(properties, codeMap, 'surface_area')
    const ownershipShares = getPropertyValue(properties, codeMap, 'ownership_shares')
    const isOccupied = getPropertyValue(properties, codeMap, 'unit_is_occupied') as unknown
    const occupation: Unit['occupation'] = isOccupied === true || isOccupied === 'true'
      ? 'occupied'
      : isOccupied === false || isOccupied === 'false' ? 'vacant' : null
    const complianceStatus = getPropertyValue(properties, codeMap, 'compliance_status') as Unit['compliance'] | null

    // Habitability comes from the habitability endpoint (one call for the
    // building), the same read as the rental exposure and the lettability gate.
    const lettable = lettableFromVerdict(verdict)

    // Extract tenant from contract stakeholders
    let tenantName: string | null = null
    let leaseEnd: string | null = null

    if (contracts && contracts.length > 0) {
      for (const contract of contracts) {
        const stakeholders = (contract as any).stakeholders as any[] | undefined
        const tenantStakeholder = stakeholders?.find(
          (s: any) =>
            s.stakeholder_type?.code?.toLowerCase() === 'tenant' ||
            s.stakeholder_type?.names?.en?.toLowerCase() === 'tenant' ||
            s.stakeholder_type?.names?.fr?.toLowerCase() === 'locataire',
        )
        if (tenantStakeholder) {
          tenantName = tenantStakeholder.user?.name || tenantStakeholder.user?.email || null
          leaseEnd = (contract as any).ended_on || (contract as any).end_date || null
          break
        }
      }
    }

    const sharesParsed = ownershipShares ? parseFloat(ownershipShares) : NaN
    return {
      uid: unitAsset.uid,
      id: unitAsset.name || unitAsset.uid.slice(0, 8),
      type: unitType || 'residential',
      area: surfaceArea ? parseFloat(surfaceArea) : 0,
      shares: Number.isFinite(sharesParsed) && sharesParsed > 0 ? sharesParsed : null,
      occupation,
      compliance: complianceStatus || 'unknown',
      lettable,
      tenant: tenantName,
      leaseEnd,
    }
  }, [properties, contracts, codeMap, unitAsset, verdict])

  return { unit, isLoading: propsLoading || contractsLoading }
}

// ─────────────────────────────────────────────────────
//  INVISIBLE DATA-FETCHING COMPONENTS
//  These render nothing but call hooks and report
//  results upward via useEffect callbacks.
// ─────────────────────────────────────────────────────

/** Fetches floor_number property for a floor asset and reports it. */
function FloorPropertyFetcher({
  org,
  floorAsset,
  codeMap,
  onLevelLoaded,
  onFetcherDone,
}: {
  org: string
  floorAsset: Asset
  codeMap: Map<string, string>
  onLevelLoaded: (floorUid: string, level: number) => void
  onFetcherDone: (floorUid: string) => void
}) {
  const { data: properties, isLoading } = useAssetProperties(org, floorAsset.uid)

  const level = useMemo(() => {
    if (isLoading || !properties) return null
    // 1) Prefer the explicit floor_number property when set.
    const val = getPropertyValue(properties, codeMap, 'floor_number')
    if (val !== null) {
      const parsed = parseInt(val, 10)
      if (!Number.isNaN(parsed)) return parsed
    }
    // 2) Fall back to parsing the asset name so demo / partially
    //    provisioned data still renders.
    return parseFloorLevelFromName(floorAsset.name)
  }, [isLoading, properties, codeMap, floorAsset.name])

  useEffect(() => {
    if (level !== null) {
      onLevelLoaded(floorAsset.uid, level)
    }
  }, [level, floorAsset.uid, onLevelLoaded])

  // Report completion even when no level could be derived, so the parent
  // can transition from a skeleton to an empty state instead of hanging.
  useEffect(() => {
    if (!isLoading) {
      onFetcherDone(floorAsset.uid)
    }
  }, [isLoading, floorAsset.uid, onFetcherDone])

  return null
}

/** Fetches child units of a floor and reports them. */
function FloorUnitCollector({
  org,
  floorAsset,
  onUnitsLoaded,
}: {
  org: string
  floorAsset: Asset
  onUnitsLoaded: (floorUid: string, units: Asset[]) => void
}) {
  const { data: unitAssets, isLoading } = useChildAssets(org, floorAsset.uid)

  useEffect(() => {
    if (!isLoading && unitAssets) {
      onUnitsLoaded(floorAsset.uid, unitAssets)
    }
  }, [isLoading, unitAssets, floorAsset.uid, onUnitsLoaded])

  return null
}

/** Fetches properties and contracts for a single unit and reports the result. */
function UnitDataCollector({
  org,
  unitAsset,
  codeMap,
  verdict,
  onUnitLoaded,
}: {
  org: string
  unitAsset: Asset
  codeMap: Map<string, string>
  verdict?: 'compliant' | 'non_compliant' | 'unknown'
  onUnitLoaded: (uid: string, unit: Unit) => void
}) {
  const { unit, isLoading } = useUnitData(org, unitAsset, codeMap, verdict)

  useEffect(() => {
    if (!isLoading) {
      onUnitLoaded(unitAsset.uid, unit)
    }
  }, [isLoading, unit, unitAsset.uid, onUnitLoaded])

  return null
}

// ─────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────
export function StackingPlan({ org, buildingUid }: StackingPlanProps) {
  const { t, i18n } = useTranslation('assets')
  const { t: tTh } = useTranslation('thematics')
  const [addKind, setAddKind] = useState<StackingEntityKind | null>(null)
  const [addColumnOpen, setAddColumnOpen] = useState(false)

  // Defer mounting the AddStackingEntityDialog out of the DropdownMenuItem's
  // onSelect handler. Radix's DropdownMenu close teardown (its FocusScope)
  // and the Dialog's own open-autofocus both run rAF-scheduled focus logic;
  // triggering the dialog synchronously from onSelect lets the two race,
  // and the dropdown's teardown can still win even with onCloseAutoFocus
  // prevented on the DropdownMenuContent below. Letting the menu's close
  // sequence finish first (a 0ms timeout, next macrotask) before mounting
  // the dialog avoids the race entirely.
  const openAddDialog = (kind: StackingEntityKind) => {
    setTimeout(() => setAddKind(kind), 0)
  }

  // "Colour by": the thematic that paints the lots (spec 2026-10-06-10). The
  // default keeps the plan's historical reading, occupation, from the one
  // occupancy property `unit_is_occupied` (spec 2026-10-07-01). The choice
  // rides in the URL (`colour_by`) and in local storage.
  const thematic = useThematic(org, buildingUid, 'stacking', 'definition:unit_is_occupied')
  const { choice } = thematic
  const [hovered, setHovered] = useState<PreparedUnit | null>(null)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })

  // Collected data from invisible fetcher components
  const [floorLevels, setFloorLevels] = useState<Map<string, number>>(new Map())
  const [floorFetchersDone, setFloorFetchersDone] = useState<Set<string>>(new Set())
  const [floorUnitsMap, setFloorUnitsMap] = useState<Map<string, Asset[]>>(new Map())
  const [unitDataMap, setUnitDataMap] = useState<Map<string, Unit>>(new Map())

  // ── Fetch property definitions for the codes we need ──
  const NEEDED_CODES = useMemo(() => ['floor_number', 'unit_type', 'surface_area', 'ownership_shares', 'unit_is_occupied', 'compliance_status'], [])
  const { data: propDefs, isLoading: propDefsLoading } = usePropertyDefinitionsByCodes(org, NEEDED_CODES)
  const codeMap = useMemo(() => buildPropertyCodeMap(propDefs || []), [propDefs])

  // ── Habitability verdict per lot, one call for the whole building ──
  const { data: habitability } = useHabitability(org, buildingUid)
  const verdictByLot = useMemo(
    () => new Map((habitability?.lots ?? []).map((l) => [l.asset_uid as string, l.verdict])),
    [habitability],
  )

  // ── Fetch asset types so we can filter building children to floors only ──
  const { data: assetTypes, isLoading: assetTypesLoading } = useAssetTypes(org)
  const assetTypeCodeMap = useMemo(() => buildAssetTypeCodeMap(assetTypes || []), [assetTypes])

  // ── Fetch floors (child assets of building) ──
  // The building's children also include non-floor equipment (elevator, SSI,
  // …) — those must never enter the stacking-plan pipeline (#1146: they were
  // mis-parsed into phantom floor rows). Filter to asset type `floor` here,
  // once, so every downstream fetcher/consumer only ever sees real floors.
  const { data: allChildAssets, isLoading: childAssetsLoading } = useChildAssets(org, buildingUid)
  const floorAssets = useMemo(
    () => (allChildAssets || []).filter((a) => assetTypeCodeMap.get(a.asset_type_uid) === 'floor'),
    [allChildAssets, assetTypeCodeMap],
  )
  const floorsLoading = childAssetsLoading || assetTypesLoading

  // ── Stable callbacks for data collector components ──
  const handleFloorLevel = useCallback((floorUid: string, level: number) => {
    setFloorLevels((prev) => {
      if (prev.get(floorUid) === level) return prev
      const next = new Map(prev)
      next.set(floorUid, level)
      return next
    })
  }, [])

  const handleFloorFetcherDone = useCallback((floorUid: string) => {
    setFloorFetchersDone((prev) => {
      if (prev.has(floorUid)) return prev
      const next = new Set(prev)
      next.add(floorUid)
      return next
    })
  }, [])

  const handleUnitsLoaded = useCallback((floorUid: string, units: Asset[]) => {
    setFloorUnitsMap((prev) => {
      const existing = prev.get(floorUid)
      if (existing && existing.length === units.length && existing.every((a, i) => a.uid === units[i].uid)) return prev
      const next = new Map(prev)
      next.set(floorUid, units)
      return next
    })
  }, [])

  const handleUnitLoaded = useCallback((uid: string, unit: Unit) => {
    setUnitDataMap((prev) => {
      const existing = prev.get(uid)
      if (existing && JSON.stringify(existing) === JSON.stringify(unit)) return prev
      const next = new Map(prev)
      next.set(uid, unit)
      return next
    })
  }, [])

  // ── Build prepared floors from collected data ──
  const floorRows = useMemo(
    () => buildFloorRows(floorAssets, floorLevels, floorUnitsMap, unitDataMap),
    [floorAssets, floorLevels, floorUnitsMap, unitDataMap],
  )

  // ── The columns lane (spec 2026-10-25-21): the building's vertical
  // columns drawn down the rows they cross, from the riser payload. Every
  // floor asset maps to its row; rows merge the floor assets of one level.
  const risersQuery = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/risers', {
    params: { path: { orgCode: org, assetUid: buildingUid } },
  })
  const riserDiagram = risersQuery.data as RiserDiagramPayload | undefined
  const columnsLayout = useMemo(() => {
    const rowByLevel = new Map(floorRows.map((row, i) => [row.level, i]))
    const rowOfLevel = new Map<string, number>()
    for (const asset of floorAssets) {
      const level = floorLevels.get(asset.uid)
      const row = level === undefined ? undefined : rowByLevel.get(level)
      if (row !== undefined) rowOfLevel.set(asset.uid, row)
    }
    return layoutStackingColumns(riserDiagram, rowOfLevel)
  }, [riserDiagram, floorRows, floorAssets, floorLevels])
  const networkColour = useMemo(
    () => new Map((riserDiagram?.networks ?? []).map((n) => [n.code, n.color])),
    [riserDiagram],
  )

  const floors = useMemo(
    (): PreparedFloor[] => floorRows.map((row) => prepareFloor(row, SW - columnsLayout.width)),
    [floorRows, columnsLayout.width],
  )

  // ── KPI computation ──
  const allUnits = useMemo(() => floors.flatMap((f) => f._units), [floors])
  const lettable = useMemo(() => allUnits.filter((u) => u.type !== 'common_area' && u.type !== 'technical'), [allUnits])
  const occupiedU = useMemo(() => lettable.filter((u) => u.occupation === 'occupied'), [lettable])
  const vacantU = useMemo(() => lettable.filter((u) => u.occupation === 'vacant'), [lettable])
  // Habitability is only counted over the lots it applies to — a building with
  // no tenancy in scope shows no counter at all rather than a reassuring zero.
  const inScopeU = useMemo(() => allUnits.filter((u) => u.lettable !== null), [allUnits])
  const notLettableU = useMemo(() => allUnits.filter((u) => u.lettable === 'forbidden'), [allUnits])
  const unknownLettableU = useMemo(() => allUnits.filter((u) => u.lettable === 'unknown'), [allUnits])
  const criticalU = useMemo(() => allUnits.filter((u) => u.compliance === 'critical'), [allUnits])
  const warningU = useMemo(() => allUnits.filter((u) => u.compliance === 'warning'), [allUnits])
  const totalArea = useMemo(() => allUnits.reduce((s, u) => s + (u.area ?? 0), 0), [allUnits])
  const occupancyRate = lettable.length > 0 ? Math.round((occupiedU.length / lettable.length) * 100) : 0

  const svgH = floors.length * (RH + RG) + VP * 2

  // Focusing a KPI card paints the plan by compliance and isolates that class.
  const focusCompliance = (value: string) => {
    if (choice.key === 'property:compliance_status' && choice.isolated === value) {
      choice.setIsolated(null)
      return
    }
    choice.setKey('property:compliance_status')
    choice.setIsolated(value)
  }
  const thematicOf = (unit: PreparedUnit) => {
    const hit = thematic.index.get(unit.uid)
    if (!hit || !choice.key) return null
    const lang = i18n.language || 'en'
    if (hit.classValue === UNSET) return { label: tTh('unset'), colour: hit.colour }
    if (hit.classValue === NOT_APPLICABLE) return { label: tTh('not_applicable'), colour: hit.colour }
    const cls = thematic.legend.data?.classes?.find((c) => c.value === hit.classValue)
    return { label: pickLabel(cls?.label, lang, hit.classValue), colour: hit.colour }
  }

  // ── Loading state ──
  const isInitialLoading = floorsLoading || propDefsLoading

  if (isInitialLoading) {
    return (
      <div className="p-7 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="mt-6 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[90px] w-full" />
          ))}
        </div>
      </div>
    )
  }

  // Collect all unit assets across all floors for data fetching
  const allFloorUnitAssets = Array.from(floorUnitsMap.values()).flat()

  return (
    <div
      className="min-h-screen mx-auto"
      style={{ maxWidth: 1200 }}
      onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
    >
      {/* Surfaces / occupation property boxes were removed here: on buildings
          every property now lives in the Data tab (data-reliability module),
          which absorbed the standalone Properties surface. */}

      {/* INVISIBLE DATA FETCHERS: floor properties (floor_number) */}
      {floorAssets?.map((floorAsset) => (
        <FloorPropertyFetcher
          key={`fp-${floorAsset.uid}`}
          org={org}
          floorAsset={floorAsset}
          codeMap={codeMap}
          onLevelLoaded={handleFloorLevel}
          onFetcherDone={handleFloorFetcherDone}
        />
      ))}

      {/* INVISIBLE DATA FETCHERS: floor children (units) */}
      {floorAssets?.map((floorAsset) => (
        <FloorUnitCollector
          key={`fu-${floorAsset.uid}`}
          org={org}
          floorAsset={floorAsset}
          onUnitsLoaded={handleUnitsLoaded}
        />
      ))}

      {/* INVISIBLE DATA FETCHERS: unit properties & contracts */}
      {allFloorUnitAssets.map((unitAsset) => (
        <UnitDataCollector
          key={`ud-${unitAsset.uid}`}
          org={org}
          unitAsset={unitAsset}
          codeMap={codeMap}
          verdict={verdictByLot.get(unitAsset.uid)}
          onUnitLoaded={handleUnitLoaded}
        />
      ))}

      {/* HEADER */}
      <div className="border-b border-border" style={{ padding: '18px 28px 0' }}>
        <div className="flex justify-end gap-2 mb-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                aria-label={t('stacking_plan.modify')}
                title={t('stacking_plan.modify')}
                data-testid="stacking-plan-modify-btn"
                className="shrink-0"
              >
                <AppIcon path={mdiPencil} size="sm" className="md:mr-1" />
                <span className="hidden md:inline">{t('stacking_plan.modify')}</span>
                <AppIcon path={mdiChevronDown} size="sm" tone="muted" className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              // Prevent Radix from restoring focus to the trigger button
              // once the menu finishes closing. Without this, the closing
              // menu's focus-return races with the just-opened
              // AddStackingEntityDialog mounting: the dialog's input shows
              // its focus ring (CSS :focus-visible carried over from
              // render), but real document focus gets yanked back to the
              // "Modifier" button, so keystrokes never reach the input.
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuItem
                onSelect={() => openAddDialog('unit')}
                data-testid="stacking-plan-add-lot-item"
              >
                {t('stacking_plan.add_lot')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => openAddDialog('floor')}
                data-testid="stacking-plan-add-floor-item"
              >
                {t('stacking_plan.add_floor')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => openAddDialog('common_area')}
                data-testid="stacking-plan-add-space-item"
              >
                {t('stacking_plan.add_space')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setTimeout(() => setAddColumnOpen(true), 0)}
                data-testid="stacking-plan-add-column-item"
              >
                {t('stacking_plan.add_column')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 24 }}>
          <div style={{ display: 'flex', gap: 2, alignItems: 'stretch', flexShrink: 0 }}>
            <KPICard label={t('stacking_plan.kpi_occupancy_rate')} value={`${occupancyRate}%`} />
            <div className="bg-border" style={{ width: 1, margin: '6px 0' }} />
            <KPICard label={t('stacking_plan.kpi_total_area')} value={formatDisplayNumber(totalArea)} sub="m²" />
            <div className="bg-border" style={{ width: 1, margin: '6px 0' }} />
            <KPICard label={t('stacking_plan.kpi_lettable_units')} value={lettable.length} sub={t('stacking_plan.kpi_vacant', { count: vacantU.length })} />
            <div className="bg-border" style={{ width: 1, margin: '6px 0' }} />
            <KPICard label={t('stacking_plan.kpi_critical')} value={criticalU.length} onClick={() => focusCompliance('critical')} active={choice.key === 'property:compliance_status' && choice.isolated === 'critical'} />
            <KPICard label={t('stacking_plan.kpi_warnings')} value={warningU.length} onClick={() => focusCompliance('warning')} active={choice.key === 'property:compliance_status' && choice.isolated === 'warning'} />
            {/* Only shown once a lot is actually in scope, so a building with
                no tenancy data carries no empty counter. */}
            {inScopeU.length > 0 && (
              <>
                <div className="bg-border" style={{ width: 1, margin: '6px 0' }} />
                <KPICard
                  label={t('stacking_plan.kpi_not_lettable')}
                  value={notLettableU.length}
                  color={LET_COLOR.forbidden}
                  sub={t('stacking_plan.kpi_lettable_unknown', { count: unknownLettableU.length })}
                />
              </>
            )}
          </div>
        </div>

        {/* COLOUR BY: the picker and the legend. The legend rows isolate a
            class and dim the other lots. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 16 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <ThematicBar org={org} state={thematic} />
            {choice.isolated && (
              <button
                onClick={() => choice.setIsolated(null)}
                data-testid="clear-filters"
                className="text-muted-foreground hover:text-foreground transition-colors"
                style={{
                  marginLeft: 'auto',
                  fontSize: 12,
                  fontFamily: "var(--font-sans)",
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {t('stacking_plan.clear_filters')} <AppIcon path={mdiClose} size="xs" style={{ display: 'inline', verticalAlign: 'middle' }} />
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* The columns lane: how many, and the one way into the detailed
                section view (spec 2026-10-25-21). */}
            {columnsLayout.columns.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="stacking-columns-legend">
                <AppIcon path={mdiArrowUpDownBold} size="xs" />
                <span>{t('stacking_plan.columns_count', { count: columnsLayout.columns.length + columnsLayout.hidden })}</span>
                <Link
                  to="."
                  search={(prev: Record<string, unknown>) => ({ ...prev, view: 'columns' })}
                  className="text-foreground underline-offset-2 hover:underline"
                  data-testid="stacking-columns-section-link"
                >
                  {t('stacking_plan.columns_section_view')}
                </Link>
              </div>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="16" height="10">
                <line x1="0" y1="5" x2="16" y2="5" style={{ stroke: V.border }} strokeWidth="1" strokeDasharray="3 3" />
              </svg>
              <span className="text-muted-foreground" style={{ fontSize: 12, fontFamily: "var(--font-sans)" }}>{t('stacking_plan.sub_floor_legend')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* SVG STACKING PLAN */}
      <div style={{ padding: '16px 28px', overflowX: 'auto' }}>
        {floors.length === 0 ? (
          floorFetchersDone.size >= (floorAssets?.length ?? 0) ? (
            <EmptyState
              variant="inline"
              className="py-12"
              title={t('stacking_plan.empty_no_floors', 'No floors found for this building.')}
            />
          ) : (
            <div className="text-muted-foreground text-center py-12 text-sm">
              {t('stacking_plan.loading')}
            </div>
          )
        ) : (
          <svg viewBox={`0 0 ${VW} ${svgH}`} width="100%" style={{ display: 'block', overflow: 'visible' }} onMouseLeave={() => setHovered(null)}>
            <defs>
              <pattern id="hatch-common_area" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45 0 0)">
                <line x1="0" y1="0" x2="0" y2="8" style={{ stroke: V.border }} strokeWidth="1.8" />
              </pattern>
              <pattern id="hatch-tech" patternUnits="userSpaceOnUse" width="14" height="14">
                <path d="M14 0L0 0 0 14" fill="none" style={{ stroke: V.border }} strokeWidth="1" />
              </pattern>
            </defs>

            {floors.map((floor, fi) => {
              const y = VP + fi * (RH + RG)
              const isAboveSub = floor.level === -1

              return (
                <g key={floor.level} className="floor-row" style={{ animationDelay: `${fi * 40}ms` }}>
                  {isAboveSub && <line x1={0} y1={y - RG / 2} x2={VW} y2={y - RG / 2} style={{ stroke: V.border }} strokeWidth="1" strokeDasharray="3 5" />}
                  <a href={`/${org}/assets/${floor.uid}`} data-testid={`stacking-plan-floor-link-${floor.label}`} className="hover:underline">
                    <text x={LW - 6} y={y + RH / 2} textAnchor="end" dominantBaseline="middle" fontSize="13" fontFamily="var(--font-sans)" style={{ fill: V.mutedForeground, cursor: 'pointer' }} fontWeight={floor.level === 0 ? '600' : '500'}>
                      {floor.label}
                    </text>
                  </a>
                  {floor.level === 0 && <rect x={LW - 2} y={y + 2} width={2} height={RH - 4} style={{ fill: V.border }} />}
                  <text x={VW - 4} y={y + RH / 2} textAnchor="end" dominantBaseline="middle" fontSize="12" fontFamily="var(--font-sans)" style={{ fill: V.mutedForeground }}>
                    {formatDisplayNumber(floor._total)} m²
                  </text>
                  {floor._units.map((unit) => {
                    const { _x: ux, _w: uw } = unit
                    const dimmed = isDimmed(thematic.index, unit.uid, choice.isolated)
                    const isHov = hovered?.uid === unit.uid
                    const clipId = `clip-${unit.uid.replace(/[^a-zA-Z0-9]/g, '_')}`

                    // The thematic paints the lot: a solid stripe and a tint of
                    // the same colour. An unset lot is grey, never absent.
                    const hit = choice.key ? thematic.index.get(unit.uid) : undefined
                    const cellColour = choice.key ? (hit?.colour ?? UNSET_COLOUR) : V.border
                    const bgFill = unit.type === 'common_area' ? 'url(#hatch-common_area)' : unit.type === 'technical' ? 'url(#hatch-tech)' : choice.key ? `color-mix(in srgb, ${cellColour} 18%, var(--card))` : 'color-mix(in srgb, var(--card), var(--accent))'

                    const showId = uw > 50
                    const showArea = uw > 50
                    // Tantièmes ride alongside the surface once the card is wide
                    // enough to fit the "120 m² · 350 ‰" pairing without clipping
                    // mid-number. Below that we keep surface only.
                    const showShares = uw > 110 && unit.shares != null
                    const showTenant = uw > 110 && unit.tenant
                    const showBadge = uw > 28

                    const specialLabel = unit.type === 'common_area' ? t('stacking_plan.special_common_area') : unit.id.startsWith('CH') ? t('stacking_plan.special_boiler_room') : unit.id.startsWith('PK') ? t('stacking_plan.special_parking') : t('stacking_plan.special_technical_room')

                    return (
                      <a key={unit.uid} href={`/${org}/assets/${unit.uid}`}>
                      <g
                        style={{
                          opacity: dimmed ? 0.12 : 1,
                          transition: 'opacity 0.2s ease',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={() => setHovered(unit)}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <defs>
                          <clipPath id={clipId}>
                            <rect x={ux + 1} y={y + 1} width={uw - 2} height={RH - 2} rx={3} />
                          </clipPath>
                        </defs>

                        <rect x={ux + 1} y={y + 1} width={uw - 2} height={RH - 2} fill={bgFill} rx={3} style={{ stroke: isHov ? V.mutedForeground : V.border }} strokeWidth={isHov ? 1.5 : 0.75} />

                        <rect x={ux + 1} y={y + 1} width={5} height={RH - 2} fill={cellColour} rx={3} clipPath={`url(#${clipId})`} data-testid={`stacking-cell-${unit.uid}`} data-class-value={hit?.classValue ?? ''} />
                        <rect x={ux + 4} y={y + 1} width={2} height={RH - 2} fill={cellColour} clipPath={`url(#${clipId})`} />

                        {showId && (
                          <text x={ux + 14} y={y + RH / 2 - (showArea ? 11 : 0)} fontSize="13" fontFamily="var(--font-sans)" style={{ fill: V.foreground }} fontWeight="600" clipPath={`url(#${clipId})`}>
                            {unit.id}
                          </text>
                        )}

                        {showArea && (
                          <text x={ux + 14} y={y + RH / 2 + 7} fontSize="14" fontFamily="var(--font-sans)" style={{ fill: V.foreground }} fontWeight="500" clipPath={`url(#${clipId})`}>
                            {unit.area && unit.area > 0
                              ? showShares
                                ? `${formatDisplayNumber(unit.area)} m² · ${formatDisplayNumber(unit.shares ?? 0)} ‰`
                                : `${formatDisplayNumber(unit.area)} m²`
                              : unit.shares != null
                                ? `${formatDisplayNumber(unit.shares)} \u2030`
                                : '\u2014'}
                          </text>
                        )}

                        {showTenant && (
                          <text x={ux + 14} y={y + RH / 2 + 26} fontSize="11" fontFamily="var(--font-sans)" style={{ fill: V.mutedForeground }} fontStyle="italic" clipPath={`url(#${clipId})`}>
                            {unit.tenant!.length > 22 ? unit.tenant!.slice(0, 20) + '\u2026' : unit.tenant}
                          </text>
                        )}

                        {(unit.type === 'common_area' || unit.type === 'technical') && uw > 80 && (
                          <text
                            x={ux + uw / 2}
                            y={y + RH / 2 + (showArea ? 22 : 4)}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="11"
                            fontFamily="var(--font-sans)"
                            style={{ fill: V.mutedForeground, opacity: 0.6 }}
                            fontStyle="italic"
                            clipPath={`url(#${clipId})`}
                          >
                            {specialLabel}
                          </text>
                        )}

                        {/* Habitability marker. Only the two states that call
                            for action are drawn — a lot that cannot lawfully be
                            let, and one whose assessment is incomplete. A lot
                            that meets every criterion says nothing, because a
                            plan covered in green badges reads as decoration. */}
                        {showBadge && (unit.lettable === 'forbidden' || unit.lettable === 'unknown') && (
                          <>
                            <circle cx={ux + uw - 14} cy={y + 14} r={9} fill={LET_COLOR[unit.lettable]} opacity={dimmed ? 0.4 : 0.9} />
                            <text
                              x={ux + uw - 14}
                              y={y + 14}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fontSize="11"
                              fontFamily="var(--font-sans)"
                              fill="white"
                              fontWeight="700"
                              data-testid={`stacking-lettable-${unit.uid}`}
                              data-lettable={unit.lettable}
                            >
                              {unit.lettable === 'forbidden' ? '⊘' : '?'}
                            </text>
                          </>
                        )}
                      </g>
                      </a>
                    )
                  })}
                </g>
              )
            })}

            {/* COLUMNS LANE: one thin bar per vertical column, down the rows
                it crosses; dashed where its continuity is not documented. */}
            {columnsLayout.columns.map(({ column, lane, spans, rows }) => {
              const x = SX + SW - columnsLayout.width + COLUMN_LANE_PAD + lane * (COLUMN_BAR_WIDTH + COLUMN_BAR_GAP)
              // A vertical circulation (stair cage, staircase, lift shaft, any
              // lift): the API reads it off the type's inheritance chain.
              const neutral = column.circulation === 'stair' || column.circulation === 'lift'
              // A riser known only by declaration has no network: its type says it.
              const colour = neutral ? V.mutedForeground : (networkColour.get(column.network) ?? COLUMN_TYPE_COLOUR[column.type_code] ?? V.mutedForeground)
              const levels = rows.map((r) => floors[r]?.label).filter(Boolean).join(', ')
              const kindType = (assetTypes ?? []).find((at) => at.code === column.type_code)
              const kind = pickLabel(kindType?.name as Record<string, string> | undefined, i18n.language || 'en', column.type_code)
              return (
                <a key={column.asset_uid} href={`/${org}/assets/${column.asset_uid}`} data-testid={`stacking-column-${column.asset_uid}`} data-circulation={column.circulation ?? ''}>
                  <title>{`${column.name} · ${kind} · ${levels}`}</title>
                  {spans.map((span) => {
                    const y1 = VP + span.fromRow * (RH + RG) + 2
                    const y2 = VP + span.toRow * (RH + RG) + RH - 2
                    return span.dashed ? (
                      <rect key={span.fromRow} x={x} y={y1} width={COLUMN_BAR_WIDTH} height={y2 - y1} rx={3} fill="none" style={{ stroke: colour }} strokeWidth={1.2} strokeDasharray="4 3" data-testid={`stacking-column-gap-${column.asset_uid}`} />
                    ) : (
                      <rect key={span.fromRow} x={x} y={y1} width={COLUMN_BAR_WIDTH} height={y2 - y1} rx={3} style={{ fill: colour, cursor: 'pointer' }} opacity={neutral ? 0.45 : 0.85} />
                    )
                  })}
                  {neutral && (
                    <text x={x + COLUMN_BAR_WIDTH / 2} y={VP + rows[0] * (RH + RG) + 14} textAnchor="middle" fontSize="9" style={{ fill: V.foreground }}>
                      {t(column.circulation === 'stair' ? 'stacking_plan.column_glyph_stairwell' : 'stacking_plan.column_glyph_elevator_shaft')}
                    </text>
                  )}
                </a>
              )
            })}
          </svg>
        )}
      </div>

      {hovered && <Tooltip unit={hovered} pos={mousePos} thematic={thematicOf(hovered)} />}

      {addColumnOpen && (
        <AddColumnDialog
          org={org}
          buildingUid={buildingUid}
          floors={floors.map((f) => ({ uid: f.uid, label: f.label }))}
          onClose={() => setAddColumnOpen(false)}
        />
      )}

      {addKind && (
        <AddStackingEntityDialog
          org={org}
          open={addKind !== null}
          onOpenChange={(o) => { if (!o) setAddKind(null) }}
          buildingUid={buildingUid}
          kind={addKind}
        />
      )}
    </div>
  )
}
